/**
 * File Operations
 *
 * Import and export functionality for IDF files.
 */

import { parseIDF, serializeIDF, type IDFDocument } from '../parser';
import type { ImportResult, ImportError, ImportWarning, ExportOptions } from './types';

/**
 * Validate and import an IDF file.
 */
export async function importIDFFile(file: File): Promise<ImportResult> {
  const filename = file.name;
  const fileSize = file.size;

  try {
    const content = await file.text();
    return validateIDFContent(content, filename, fileSize);
  } catch (error) {
    return {
      success: false,
      errors: [
        {
          message: `Failed to read file: ${error instanceof Error ? error.message : 'Unknown error'}`,
          recoverable: false,
        },
      ],
      warnings: [],
      canProceed: false,
      filename,
      fileSize,
    };
  }
}

/**
 * Validate IDF content and return import result.
 */
export function validateIDFContent(
  content: string,
  filename?: string,
  fileSize?: number
): ImportResult {
  const result = parseIDF(content);

  const errors: ImportError[] = result.errors
    .filter((e) => e.severity === 'error')
    .map((e) => ({
      message: e.message,
      line: e.line,
      recoverable: false,
    }));

  const warnings: ImportWarning[] = result.errors
    .filter((e) => e.severity === 'warning')
    .map((e) => ({
      message: e.message,
      line: e.line,
      type: categorizeWarning(e.message),
    }));

  // Extract version from parsed document
  const versionObj = result.document.objects.find(
    (obj) => obj.className.toLowerCase() === 'version'
  );
  const version = versionObj?.fields[0]?.value ?? '';

  // Determine if import can proceed
  // For now, we allow proceeding even with errors if the document has objects
  const canProceed = result.document.objects.length > 0;

  return {
    success: result.success,
    errors,
    warnings,
    canProceed,
    objectCount: result.document.objects.length,
    version,
    filename,
    fileSize,
    content: canProceed ? content : undefined,
  };
}

/**
 * Categorize a warning message by type.
 */
function categorizeWarning(
  message: string
): 'unknown-object' | 'deprecated-field' | 'syntax' | 'other' {
  const lower = message.toLowerCase();
  if (lower.includes('unknown') || lower.includes('unrecognized')) {
    return 'unknown-object';
  }
  if (lower.includes('deprecated')) {
    return 'deprecated-field';
  }
  if (lower.includes('syntax') || lower.includes('expected')) {
    return 'syntax';
  }
  return 'other';
}

/**
 * Export an IDF document with the given options.
 */
export function exportIDF(document: IDFDocument, options: ExportOptions): string {
  // Create a copy of the document for potential sorting
  let docToExport = document;

  if (options.sortObjects) {
    docToExport = {
      ...document,
      objects: [...document.objects].sort((a, b) =>
        a.className.localeCompare(b.className)
      ),
    };
  }

  // Serialize with appropriate options
  let content = serializeIDF(docToExport, {
    minified: false,
    alignComments: options.includeComments,
  });

  // Remove comments if requested
  if (!options.includeComments) {
    content = removeComments(content);
  }

  // Convert line endings if needed
  if (options.lineEndings === 'windows') {
    content = content.replace(/\n/g, '\r\n');
  }

  return content;
}

/**
 * Remove all comments from IDF content.
 */
function removeComments(content: string): string {
  const lines = content.split('\n');
  const filteredLines: string[] = [];

  for (const line of lines) {
    // Skip standalone comment lines
    if (line.trim().startsWith('!')) {
      continue;
    }

    // Remove inline comments
    const commentIndex = line.indexOf('!');
    if (commentIndex !== -1) {
      // Keep the part before the comment, but preserve trailing comma/semicolon
      const beforeComment = line.substring(0, commentIndex).trimEnd();
      if (beforeComment) {
        filteredLines.push(beforeComment);
      }
    } else {
      filteredLines.push(line);
    }
  }

  return filteredLines.join('\n');
}

/**
 * Download content as a file.
 */
export function downloadFile(content: string, filename: string, mimeType = 'text/plain'): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';

  document.body.appendChild(a);
  a.click();

  // Cleanup
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Read a file as text.
 */
export function readFileAsText(file: File): Promise<string> {
  return file.text();
}

/**
 * Read a file as Data URL (for images).
 */
export function readFileAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => { resolve(reader.result as string); };
    reader.onerror = () => { reject(new Error('Failed to read file')); };
    reader.readAsDataURL(file);
  });
}

/**
 * Format file size in human-readable format.
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${String(bytes)} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Get file extension from filename.
 */
export function getFileExtension(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  if (lastDot === -1) {
    return '';
  }
  return filename.substring(lastDot + 1).toLowerCase();
}

/**
 * Check if a file is an IDF file by extension.
 */
export function isIDFFile(filename: string): boolean {
  const ext = getFileExtension(filename);
  return ext === 'idf' || ext === 'imf';
}

/**
 * Check if a file is an EPW weather file by extension.
 */
export function isWeatherFile(filename: string): boolean {
  const ext = getFileExtension(filename);
  return ext === 'epw';
}

/**
 * Generate a safe filename from a model name.
 */
export function sanitizeFilename(name: string): string {
  // Remove or replace invalid characters
  return name
    .replace(/[<>:"/\\|?*]/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .trim();
}
